/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { t } from 'i18next';
import { UseFormTrigger } from 'react-hook-form';

import { isProductMassApplicable } from '../../../../../../../../../../shared/helpers/product-mass-helpers';
import { DeclaredUnit } from '../../../../../../../../../../shared/interfaces/IComponentDetails';
import { IProcessDetails } from '../../../../../../../../../../shared/interfaces/IFootprint';
import {
  IMachine,
  MachineType,
  OperationMode,
  PROCESS_DROPDOWN_OPTIONS,
  ProcessIdsMap,
} from '../../PcfWizard.type';

/**
 * An Individual Process no longer has a user-facing machine, but the API still expects the single
 * main machine to carry a name. This is the value it gets; it is never shown in the form.
 */
export const DEFAULT_INDIVIDUAL_PROCESS_MACHINE_NAME = 'Machine';

export const MACHINE_LIST_BASED_ON_PROCESS: Record<
  string,
  Array<
    Omit<
      IMachine,
      | 'action'
      | 'number_of_cavities'
      | 'shot_weight'
      | 'cycle_time'
      | 'manufacturer_id'
      | 'operation_value'
      | 'rated_power'
      | 'comment'
    > & {
      rated_power?: number | null;
      operation_value?: number | null;
      manufacturer_id?: number | null;
      cycle_time?: number | null;
      number_of_cavities?: number | null;
      shot_weight?: number | null;
      comment?: string | null;
    }
  >
> = {
  [PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]: [
    {
      type: MachineType.MAIN,
      manufacturer_id: null,
      model: '',
      rated_power: null,
      cycle_time: null,
      number_of_cavities: null,
      shot_weight: null,
    },
    {
      type: MachineType.AUXILIARY,
      name: 'Hopper Dryer',
      operation_mode: OperationMode.DEFAULT,
      rated_power: null,
      operation_value: null,
    },
    {
      type: MachineType.AUXILIARY,
      name: 'Mold Temperature Controller',
      operation_mode: OperationMode.DEFAULT,
      rated_power: null,
      operation_value: null,
    },
    {
      type: MachineType.AUXILIARY,
      name: 'Hot Runner System',
      operation_mode: OperationMode.DEFAULT,
      rated_power: null,
      operation_value: null,
    },
    {
      type: MachineType.AUXILIARY,
      name: 'Robot',
      operation_mode: OperationMode.HANDLING_TIME,
      rated_power: null,
      operation_value: null,
    },
  ],
  [PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]: [
    {
      type: MachineType.MAIN,
      name: DEFAULT_INDIVIDUAL_PROCESS_MACHINE_NAME,
      comment: '',
      energy_sources: [{ source: null, amount: null }],
    },
  ],
};

export const GENERAL_INFORMATION_SECTION_FIELD_NAMES = [
  'unitary_product_amount',
  'declared_unit',
  'product_mass_per_declared_unit',
  'partial_full_pcf',
  'retro_or_prospective_pcf_type',
  'reference_period',
  'validity_period_end',
  'use_of_energy',
];

// The values are watched positionally, so resolve the indexes from the field names
const DECLARED_UNIT_INDEX = GENERAL_INFORMATION_SECTION_FIELD_NAMES.indexOf('declared_unit');
const PRODUCT_MASS_INDEX = GENERAL_INFORMATION_SECTION_FIELD_NAMES.indexOf(
  'product_mass_per_declared_unit',
);

// Function to check if all General Information Section fields are filled & valid.
// Takes the product mass into account too
export const checkGeneralInformationValidity = async (
  generalInformationValues: any[],
  trigger: UseFormTrigger<any>,
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  // Get all values except product mass
  const generalInformationValuesWithoutProductMass = generalInformationValues.filter((_, index) => {
    return index !== PRODUCT_MASS_INDEX;
  });

  // Check if all fields except product mass filled
  let isAllFilled = generalInformationValuesWithoutProductMass.every((value) => {
    return !!value;
  });

  // Get declared unit value
  const declaredUnitValue = generalInformationValues[DECLARED_UNIT_INDEX] as DeclaredUnit;

  // Check if product mass is required, check if it's filled
  const isProductMassRequired = isProductMassApplicable(declaredUnitValue);

  // Check if product mass is filled if and only if it is required
  if (isProductMassRequired) {
    const productMassValue = generalInformationValues[PRODUCT_MASS_INDEX];

    // Update isAllFilled based on product mass
    isAllFilled = isAllFilled && !!productMassValue;
  }

  // Check the validity of the fields if and only if they are filled
  let isAllValid = false;

  if (isAllFilled) {
    if (isProductMassRequired) {
      isAllValid = await trigger(GENERAL_INFORMATION_SECTION_FIELD_NAMES);
    } else {
      isAllValid = await trigger(
        GENERAL_INFORMATION_SECTION_FIELD_NAMES.filter((name) => {
          return name !== 'product_mass_per_declared_unit';
        }),
      );
    }
  }
  // Update the state variable
  setIsDisabled(!(isAllFilled && isAllValid));
};

export const getProcessDetailsBasedOnProcessId = (
  selectedValue: PROCESS_DROPDOWN_OPTIONS,
  order: number,
):
  | (Omit<IProcessDetails, 'machines'> & {
      machines?: Array<
        Omit<
          IMachine,
          | 'action'
          | 'number_of_cavities'
          | 'shot_weight'
          | 'cycle_time'
          | 'manufacturer_id'
          | 'operation_value'
          | 'rated_power'
          | 'comment'
        > & {
          rated_power?: number | null;
          operation_value?: number | null;
          manufacturer_id?: number | null;
          cycle_time?: number | null;
          number_of_cavities?: number | null;
          shot_weight?: number | null;
          comment?: string | null;
        }
      >;
    })
  | undefined => {
  switch (selectedValue) {
    case PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS:
      return {
        name: '',
        order,
        process_id: ProcessIdsMap[selectedValue as keyof typeof ProcessIdsMap],
        machines:
          MACHINE_LIST_BASED_ON_PROCESS[
            selectedValue as keyof typeof MACHINE_LIST_BASED_ON_PROCESS
          ] || [],
        input_materials: [],
      };
    case PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING:
      return {
        name: t('footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.title'),
        order,
        process_id: ProcessIdsMap[selectedValue as keyof typeof ProcessIdsMap],
        machines:
          MACHINE_LIST_BASED_ON_PROCESS[
            selectedValue as keyof typeof MACHINE_LIST_BASED_ON_PROCESS
          ] || [],
        input_materials: [],
      };
    case PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS:
      return {
        name: t('footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.title'),
        order,
        process_id: ProcessIdsMap[selectedValue as keyof typeof ProcessIdsMap],
        route_name: '',
        starting_point: {
          name: '',
          coordinates: {
            latitude: undefined,
            longitude: undefined,
          },
        },
        end_location: {
          name: '',
          coordinates: {
            latitude: undefined,
            longitude: undefined,
          },
        },
      };

    default:
      return undefined;
  }
};
